import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { combineLatest, from, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { License } from 'src/app/models/licenses.model';
import { User } from 'src/app/models/user.model';
import { LicenseService } from 'src/app/services/license/license.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { PatientService } from 'src/app/services/patient.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-provider-selector-list',
  templateUrl: './provider-selector-list.component.html',
  styleUrls: ['./provider-selector-list.component.scss'],
})
export class ProviderSelectorListComponent implements OnInit, OnDestroy {
  /** The form control bound to the mat-select */
  @Input() providerControl!: UntypedFormControl;

  /** Label text shown above the select */
  @Input() label = 'Primary Provider';

  /** Appearance for the mat-form-field (outline, fill, etc.) */
  @Input() appearance: 'legacy' | 'standard' | 'fill' | 'outline' = 'outline';

  /** Optional hint text displayed below the field */
  @Input() hint = 'Please select provider';

  /** Whether to show the hint text */
  @Input() showHint = false;

  /** Whether to include an “All” option */
  @Input() showAll = false;

  /** Optional placeholder for the select */
  @Input() placeholder = '';

  /** Optional external provider list — skips fetching if provided */
  @Input() providers: User[] = [];

  /** Whether to only include clients (default true) */
  @Input() filterClientsOnly = true;

  /** Customizable error message text */
  @Input() errorMessage = 'You must assign a provider';

  /** Property name to use as the select value (optional, e.g. 'user_id') */
  @Input() valueKey?: string;

  /** Whether to show provider languages */
  @Input() showLanguages = false;

  /** Optional custom label prefix before the languages */
  @Input() languagesLabel = 'Speaks';
  statesWithoutCompactLicense: { abbreviation: string; name: string }[];
  private unsubscribe$ = new Subject<void>();

  constructor(
    private dataModel: InitialDataModel,
    private utilsService: UtilsService,
    private patientService: PatientService,
    private licenseService: LicenseService
  ) {}

  async ngOnInit(): Promise<void> {
    this.statesWithoutCompactLicense = (await this.utilsService.getStatesWithoutCompactLicense().toPromise()).states;
    // If providers weren't passed in, fetch them
    if (!this.providers.length) {
      this.listenForProviders();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  checkProviderLicense(patientAddressState: string, licenses: License[]): boolean {
    try {
      const unexpiredLicenses = licenses?.filter((ls) => new Date(ls.expirationDate) > new Date()) ?? [];
      const statesWithoutCompactLicense = this.statesWithoutCompactLicense || [];
      const hasPatientStateLicense = unexpiredLicenses.some((ls) => ls.state.includes(patientAddressState));
      const hasCompactLicense = unexpiredLicenses.some((l) => l.isCompact);
      const patientInNonCompactState = statesWithoutCompactLicense.some(
        (state) => state.abbreviation === patientAddressState
      );

      if (!hasPatientStateLicense && (!hasCompactLicense || patientInNonCompactState)) {
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking provider license:', error);
      return true;
    }
  }

  /**
   * Custom comparison function for mat-select to compare provider objects
   * This is needed because we're working with objects and need to compare by user_id
   */
  compareProviders(provider1: User | string, provider2: User | string): boolean {
    if (provider1 === 'ALL' && provider2 === 'ALL') {
      return true;
    }
    if (!provider1 || !provider2) {
      return false;
    }
    return (provider1 as User).user_id === (provider2 as User).user_id;
  }

  /** Subscribe to provider panel updates */
  private listenForProviders(): void {
    combineLatest([this.dataModel.clientProviderPanel$, this.patientService.currentPatient$])
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(([panel, patient]) => !!panel),
        switchMap(([panel, patient]) => {
          const providers = (this.filterClientsOnly ? panel.filter((client) => client.roles.isClient) : panel).sort(
            (a, b) => this.utilsService.sortUsersByAlphabeticLastNameOrder(a, b)
          );

          // if no patient, just return providers (no license check)
          if (!patient) {
            return from([providers]);
          }

          const patientAddressState =
            patient?.addresses?.find((a) => a.primary)?.state ?? patient?.addresses?.[0]?.state ?? '';

          if (!patientAddressState) {
            return from([providers]);
          }

          const providersWithLicense = providers.map((provider) => ({
            ...provider,
            hasLicense: this.checkProviderLicense(patientAddressState, provider.licenses),
          }));

          return from([providersWithLicense]);
        })
      )
      .subscribe((providersWithLicense) => {
        this.providers = providersWithLicense;
      });
  }
}
